package com.rainbowclubs.rainbow.presentation.admin

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.rainbowclubs.rainbow.data.models.MembershipDataItem
import com.rainbowclubs.rainbow.data.models.MembershipDataTypes
import com.rainbowclubs.rainbow.data.requests.UpdateClubRequest
import com.rainbowclubs.rainbow.domain.repositories.ClubImageUpload
import com.rainbowclubs.rainbow.domain.use_cases.club.GetClubUseCase
import com.rainbowclubs.rainbow.domain.use_cases.club.UpdateClubUseCase
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.flow.*
import kotlinx.coroutines.launch
import rainbowclubs.composeapp.generated.resources.Res
import rainbowclubs.composeapp.generated.resources.club_field_label_required
import rainbowclubs.composeapp.generated.resources.club_form_error
import rainbowclubs.composeapp.generated.resources.club_name_required

class ManageClubViewModel(
    private val clubId: String,
    private val getClubUseCase: GetClubUseCase,
    private val updateClubUseCase: UpdateClubUseCase,
) : ViewModel() {

    sealed class NavigationEvent {
        object Back : NavigationEvent()
    }

    private val _uiState = MutableStateFlow(ManageClubUiState())
    val uiState: StateFlow<ManageClubUiState> = _uiState.asStateFlow()
    private var selectedImage: ClubImageUpload? = null

    private val navigationIntentChannel = Channel<NavigationEvent>(Channel.BUFFERED)
    val navigationIntent: Flow<NavigationEvent> = navigationIntentChannel.receiveAsFlow()

    init {
        fetchClub()
    }

    fun fetchClub() {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(isLoading = true, formError = null)
            getClubUseCase(clubId)
                .onSuccess { club ->
                    _uiState.value = ManageClubUiState(
                        isLoading = false,
                        name = club.name,
                        description = club.description.orEmpty(),
                        membershipData = club.membershipData.orEmpty(),
                        clubImageUrl = club.clubImageUrl,
                    )
                    loadClubImage(club.clubImageUrl)
                }
                .onFailure {
                    _uiState.value = _uiState.value.copy(
                        isLoading = false,
                        formError = Res.string.club_form_error,
                    )
                }
        }
    }

    fun onBackClick() {
        navigationIntentChannel.trySend(NavigationEvent.Back)
    }

    fun onNameChanged(value: String) {
        _uiState.value = _uiState.value.copy(
            name = value,
            nameError = null,
            formError = null,
        )
    }

    fun onDescriptionChanged(value: String) {
        _uiState.value = _uiState.value.copy(
            description = value,
            formError = null,
        )
    }

    fun onImageSelected(image: ClubImageUpload) {
        selectedImage = image
        _uiState.value = _uiState.value.copy(
            clubImageBytes = image.bytes,
            selectedImageName = image.fileName,
            formError = null,
        )
    }

    fun onAddMembershipFieldClick() {
        _uiState.value = _uiState.value.copy(
            editor = MembershipDataEditorUiState(
                index = null,
                label = "",
                type = MembershipDataTypes.SHORT_TEXT,
                required = true,
            ),
            formError = null,
        )
    }

    fun onEditMembershipFieldClick(index: Int) {
        val item = _uiState.value.membershipData.getOrNull(index) ?: return
        _uiState.value = _uiState.value.copy(
            editor = MembershipDataEditorUiState(
                index = index,
                label = item.label,
                type = item.type,
                required = item.required,
            ),
            formError = null,
        )
    }

    fun onDeleteMembershipFieldClick(index: Int) {
        _uiState.value = _uiState.value.copy(
            membershipData = _uiState.value.membershipData.filterIndexed { i, _ -> i != index },
            formError = null,
        )
    }

    fun onEditorLabelChanged(value: String) {
        val editor = _uiState.value.editor ?: return
        _uiState.value = _uiState.value.copy(
            editor = editor.copy(
                label = value,
                labelError = null,
            ),
        )
    }

    fun onEditorRequiredChanged(required: Boolean) {
        val editor = _uiState.value.editor ?: return
        _uiState.value = _uiState.value.copy(editor = editor.copy(required = required))
    }

    fun onEditorTypeChanged(type: MembershipDataTypes) {
        val editor = _uiState.value.editor ?: return
        _uiState.value = _uiState.value.copy(editor = editor.copy(type = type))
    }

    fun dismissMembershipEditor() {
        _uiState.value = _uiState.value.copy(editor = null)
    }

    fun saveMembershipEditor() {
        val editor = _uiState.value.editor ?: return
        val label = editor.label.trim()
        if (label.isBlank()) {
            _uiState.value = _uiState.value.copy(
                editor = editor.copy(labelError = Res.string.club_field_label_required),
            )
            return
        }

        val currentItems = _uiState.value.membershipData
        val existingOptions = editor.index
            ?.let { currentItems.getOrNull(it)?.options }
            ?.takeIf { editor.type == MembershipDataTypes.DROP_DOWN || editor.type == MembershipDataTypes.MULTI_SELECT }

        val item = MembershipDataItem(
            label = label,
            type = editor.type,
            options = existingOptions,
            required = editor.required,
        )

        val updatedItems = if (editor.index == null) {
            currentItems + item
        } else {
            currentItems.mapIndexed { index, current ->
                if (index == editor.index) item else current
            }
        }

        _uiState.value = _uiState.value.copy(
            membershipData = updatedItems,
            editor = null,
            formError = null,
        )
    }

    fun saveClub() {
        val state = _uiState.value
        val name = state.name.trim()
        if (name.isBlank()) {
            _uiState.value = state.copy(nameError = Res.string.club_name_required)
            return
        }

        viewModelScope.launch {
            _uiState.value = state.copy(isSubmitting = true, formError = null)
            updateClubUseCase(
                clubId = clubId,
                request = UpdateClubRequest(
                    name = name,
                    description = state.description.trim(),
                    membershipData = state.membershipData,
                ),
                image = selectedImage,
            ).onSuccess {
                navigationIntentChannel.send(NavigationEvent.Back)
            }.onFailure {
                _uiState.value = state.copy(
                    isSubmitting = false,
                    formError = Res.string.club_form_error,
                )
            }
        }
    }

    private fun loadClubImage(imageUrl: String?) {
        if (imageUrl.isNullOrBlank()) {
            return
        }

        viewModelScope.launch {
            getClubUseCase.image(imageUrl)
                .onSuccess { bytes ->
                    _uiState.value = _uiState.value.copy(clubImageBytes = bytes)
                }
        }
    }
}
